import { useState } from 'react'
import { Card, Button, Input } from './UI'
import { Search } from 'lucide-react'

interface Column<T> {
  key: keyof T
  label: string
  render?: (value: any, row: T) => React.ReactNode
}

interface Action<T> {
  icon: any
  label: string
  onClick: (row: T) => void
  variant?: 'primary' | 'outline' | 'ghost'
}

interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  actions?: Action<T>[]
  onSearch?: (term: string) => void
  loading?: boolean
  emptyMessage?: string
}

export function DataTable<T extends { id: string }>({
  data,
  columns,
  actions = [],
  onSearch,
  loading,
  emptyMessage = 'No data found'
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('')

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    onSearch?.(value)
  }

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card>
      {onSearch && (
        <div className="mb-4">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e: { target: { value: string } }) => handleSearch(e.target.value)}
            icon={Search}
          />
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              {columns.map(col => (
                <th key={String(col.key)} className="text-left py-3 px-4 font-medium text-gray-600">
                  {col.label}
                </th>
              ))}
              {actions.length > 0 && (
                <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                {columns.map(col => (
                  <td key={String(col.key)} className="py-3 px-4">
                    {col.render ? col.render(row[col.key], row) : String(row[col.key] || '')}
                  </td>
                ))}
                {actions.length > 0 && (
                  <td className="py-3 px-4">
                    <div className="flex space-x-2">
                      {actions.map((action, idx) => (
                        <Button
                          key={idx}
                          size="sm"
                          variant={action.variant || 'ghost'}
                          icon={action.icon}
                          onClick={() => action.onClick(row)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">{emptyMessage}</div>
      )}
    </Card>
  )
}